import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  cancelMemberInvitation,
  listMembers,
  removeMember,
  resendMemberInvitation,
  updateMemberRole,
  updateMemberStatus,
} from "@/lib/api/members";
import {
  listInvitations,
  inviteMember,
  inviteMemberViaH2j,
  cancelInvitation,
  resendInvitation,
} from "@/lib/api/invitations";
import type { InviteMemberInput, MemberStatus, SsoInviteMemberInput } from "@repo/shared";

export const memberKeys = {
  all: ["organization-members"] as const,
  members: (orgId: string) => [...memberKeys.all, orgId, "members"] as const,
  invitations: (orgId: string) => [...memberKeys.all, orgId, "invitations"] as const,
};

export const membersQueryOptions = (organizationId: string) =>
  queryOptions({
    queryKey: memberKeys.members(organizationId),
    queryFn: () => listMembers(organizationId),
    enabled: !!organizationId,
  });

export const invitationsQueryOptions = (organizationId: string) =>
  queryOptions({
    queryKey: memberKeys.invitations(organizationId),
    queryFn: () => listInvitations(organizationId),
    enabled: !!organizationId,
  });

export function useInviteMember(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InviteMemberInput) => inviteMember(organizationId, data.email, data.role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.invitations(organizationId) });
    },
  });
}

/** Login H2J (#700): the new member shows up in the list, pending activation, so both lists refresh. */
export function useInviteMemberViaH2j(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SsoInviteMemberInput) => inviteMemberViaH2j(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.members(organizationId) });
      queryClient.invalidateQueries({ queryKey: memberKeys.invitations(organizationId) });
    },
  });
}

export function useResendMemberInvitation() {
  return useMutation({ mutationFn: (memberId: string) => resendMemberInvitation(memberId) });
}

export function useCancelMemberInvitation(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberId: string) => cancelMemberInvitation(memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.members(organizationId) });
      queryClient.invalidateQueries({ queryKey: memberKeys.invitations(organizationId) });
    },
  });
}

export function useResendInvitation(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: InviteMemberInput["role"] }) =>
      resendInvitation(organizationId, email, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.invitations(organizationId) });
    },
  });
}

export function useCancelInvitation(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) => cancelInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.invitations(organizationId) });
    },
  });
}

export function useRemoveMember(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberIdOrEmail: string) => removeMember(memberIdOrEmail, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.members(organizationId) });
    },
  });
}

export function useUpdateMemberRole(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) =>
      updateMemberRole(memberId, role, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.members(organizationId) });
    },
  });
}

export function useUpdateMemberStatus(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, status }: { memberId: string; status: MemberStatus }) =>
      updateMemberStatus(memberId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.members(organizationId) });
    },
  });
}
